/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { CluMigrationStore } from 'src/clu/core/stores/CluMigrationStore';
import { CluProjectStore } from 'src/clu/core/stores/CluProjectStore';
import { AppStore } from 'src/core/stores/app/AppStore';
import { AuthStore } from 'src/core/stores/AuthStore';
import { AzureExpStore } from 'src/core/stores/AzureExpStore';
import { ServiceKey } from 'src/core/stores/AzureSessionStore';
import { AzureStore } from 'src/core/stores/AzureStore';
import { ConsentStore } from 'src/core/stores/ConsentStore';
import { CultureStore } from 'src/core/stores/CultureStore';
import { CustomSkillGlobalsStore } from 'src/core/stores/CustomSkillGlobalsStore';
import { UserStore } from 'src/core/stores/UserStore';
import { isProductionEnvironment } from 'src/core/utils/api/environment';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { CustomTextProjectStore } from 'src/customText/core/stores/CustomTextProjectStore';
import { LuisAppStore } from 'src/luis/core/stores/LuisAppStore';
import { createPrebuiltEntityStoreHouse } from 'src/luis/core/stores/prebuiltEntityStoreHouse';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { createToastStoreHouse } from 'src/luis/core/stores/toastStoreHouse';
import { QnaMakerProjectStore } from 'src/qnaMaker/core/stores/QnaMakerProjectStore';
import { TranslationJobStore } from 'src/translation/stores/TranslationJobStore';
import { UserMigrationInfoStore } from 'src/verticals/azureMigrationModal/stores/UserMigrationInfoStore';

export class RootStore {
    public readonly authStore = new AuthStore();

    public readonly azureStore = new AzureStore();

    public readonly consentStore = new ConsentStore();

    public readonly cluProjectStoreHouse = createResourceStoreHouse<CluProjectStore>();

    public readonly luisAppStoreHouse = createResourceStoreHouse<LuisAppStore>();

    public readonly qnaProjectStoreHouse = createResourceStoreHouse<QnaMakerProjectStore>();

    public readonly customTextGlobalsStoreHouse = createResourceStoreHouse<CustomSkillGlobalsStore>();

    public readonly summarizationGlobalsStoreHouse = createResourceStoreHouse<CustomSkillGlobalsStore>();

    public readonly sentimentGlobalsStoreHouse = createResourceStoreHouse<CustomSkillGlobalsStore>();

    public readonly cluGlobalsStoreHouse = createResourceStoreHouse<CustomSkillGlobalsStore>();

    public readonly customTextProjectStoreHouse = createResourceStoreHouse<CustomTextProjectStore>();

    public readonly cluConversationCultureStoreHouse = createResourceStoreHouse<CultureStore>();

    public readonly cluOrchestrationCultureStoreHouse = createResourceStoreHouse<CultureStore>();

    public readonly luisCultureStoreHouse = createResourceStoreHouse<CultureStore>();

    public readonly customTextCultureStorehouse = createResourceStoreHouse<CultureStore>();

    public readonly summarizationCultureStorehouse = createResourceStoreHouse<CultureStore>();

    public readonly sentimentCultureStorehouse = createResourceStoreHouse<CultureStore>();

    public readonly translationJobStoreHouse = createResourceStoreHouse<TranslationJobStore>();

    public readonly toastStoreHouse = createToastStoreHouse();

    public readonly prebuiltEntityStoreHouse = createPrebuiltEntityStoreHouse();

    public readonly userMigrationInfoStoreHouse = createResourceStoreHouse<UserMigrationInfoStore>();

    public readonly cluMigrationStoreHouse = new CluMigrationStore();

    @observable public browserType: string = null;

    @observable public userStore: UserStore = null;

    @observable public hasPermissionToListLUISApps = true;

    @observable public currentLuisAppStore: LuisAppStore = null;

    @observable public currentCustomTextProjectStore: CustomTextProjectStore = null;

    @observable public currentCluProjectStore: CluProjectStore = null;

    @observable public currentQnaMakerProjectStore: QnaMakerProjectStore;

    @observable public azureExpStore = new AzureExpStore(null, null, []);

    @observable public shouldHideBanner = sessionStorage.getItem('shouldHideBanner');

    @observable public shouldHideLuisDeprecationBanner = sessionStorage.getItem('shouldHideLuisDeprecationBanner');

    @observable public shouldHideLanguageStudioDeprecationBanner = sessionStorage.getItem('shouldHideLanguageStudioDeprecationBanner');

    @observable public accessError: { show: boolean; statusCode?: number } = { show: false, statusCode: undefined };

    @computed
    public get currentAppStore(): AppStore {
        return this.currentLuisAppStore;
    }

    /**
     * Returns the service key to use to call apis.
     */
    @computed
    public get serviceKey(): ServiceKey {
        const portal = getPortalFromUrl();
        const csAccount = this.azureStore.session.csAccountStore;

        const productionServiceKey = this.azureStore.session.serviceKey;

        const stagingServiceKey: ServiceKey = {
            ...productionServiceKey,
            value: csAccount?.id,
            keyType: csAccount?.id ? 'resourceKey' : 'none'
        };
        if (portal.subPath === 'qnaMaker') {
            return productionServiceKey;
        }

        if (csAccount?.kind === 'TextTranslation') {
            return productionServiceKey;
        }

        if (portal.kind === 'languageStudio' && portal.subPath === 'root') {
            return productionServiceKey;
        }

        if (portal.kind === 'demos') {
            return productionServiceKey;
        }

        if (isProductionEnvironment()) {
            return productionServiceKey;
        }

        return stagingServiceKey;
    }

    @action
    public setShouldHideBanner() {
        this.shouldHideBanner = 'true';
    }

    @action
    public setShouldHideLuisDeprecationBanner() {
        this.shouldHideLuisDeprecationBanner = 'true';
    }

    @action
    public setShouldHideLanguageStudioDeprecationBanner() {
        this.shouldHideLanguageStudioDeprecationBanner = 'true';
    }

    @action
    public setAccessError(show: boolean, statusCode?: number) {
        this.accessError = { show, statusCode };
    }

    @action
    public setBrowserType(browserType: string) {
        this.browserType = browserType;
    }

    @action
    public setHasPermissionToListLUISApps(value: boolean): void {
        this.hasPermissionToListLUISApps = value;
    }

    @action
    public setUserStore(userStore: UserStore): void {
        this.userStore = userStore;
    }

    @action
    public setCurrentLuisAppStore(conversationAppStore: LuisAppStore): void {
        this.currentLuisAppStore = conversationAppStore;
    }

    @action
    public setCurrentCustomTextProjectStore(customTextProjectStore: CustomTextProjectStore): void {
        this.currentCustomTextProjectStore = customTextProjectStore;
    }

    @action
    public setCurrentCluProjectStore(cluProjectStore: CluProjectStore): void {
        this.currentCluProjectStore = cluProjectStore;
    }

    @action
    public setCurrentQnaProjectStore(qnaProjectStore: QnaMakerProjectStore): void {
        this.currentQnaMakerProjectStore = qnaProjectStore;
    }

    @action
    public setAzureExpStore(azureExp: AzureExpStore): void {
        this.azureExpStore = azureExp;
    }
}
